/**
 * ============================================================================
 * DeviationActions — Phase 7 frontend (Deviation Management Module). F1-3.
 * Owner: drafts/61 remediation.
 *
 * The investigator/QA action surface for one deviation. Renders status-aware
 * forms wired to the existing real-PG endpoints:
 *   - Triage   (REPORTED)                         → POST /deviations/{id}/triage
 *   - Add note (any non-terminal status)          → POST /deviations/{id}/notes
 *   - Close    (TRIAGED / INVESTIGATION / RCA)     → POST /deviations/{id}/close
 *
 * GMP gates mirrored from the server: non-MINOR closure needs an RCA summary
 * ≥ 50 chars (DEV-008); CRITICAL closure needs two DISTINCT signatures (DEV-005).
 *
 * URS rows: DEV-002, DEV-004 (triage), DEV-005 (dual-sign), DEV-008 (RCA).
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { apiUrl, buildHeaders } from '../../api/client';
import { useSession } from '../../auth/SessionProvider';
import styles from './DeviationDetail.module.css';
import type { DeviationDetailPayload, NoteKind } from './useDeviationDetailQuery';
import type { DeviationClassification } from '../DeviationTriage/useDeviationsQuery';

export interface DeviationActionsProps {
  d: DeviationDetailPayload;
  onChanged: () => void;
}

const CLASSIFICATIONS: DeviationClassification[] = ['CRITICAL', 'MAJOR', 'MINOR'];
const NOTE_KINDS: NoteKind[] = [
  'OBSERVATION',
  'EVIDENCE_LINK',
  'INTERVIEW',
  'RCA_HYPOTHESIS',
  'RCA_CONFIRMED',
  '5_WHY',
  'FISHBONE',
];

export function DeviationActions({ d, onChanged }: DeviationActionsProps) {
  const { t } = useTranslation();
  const session = useSession();
  const me = session.session?.userId ?? '';

  const isTerminal = d.status === 'CLOSED' || d.status === 'CANCELLED';
  const canTriage = d.status === 'REPORTED';
  const canClose = ['TRIAGED', 'INVESTIGATION', 'RCA'].includes(d.status);

  if (isTerminal) {
    // Nothing actionable once closed/cancelled — the read-only panels cover it.
    return null;
  }

  return (
    <section className={styles.panel} aria-label={t('deviation.actions.aria', { defaultValue: 'Deviation actions' })}>
      <h2 className={styles.panelTitle}>
        {t('deviation.actions.title', { defaultValue: 'Actions' })}
      </h2>
      {canTriage && <TriageForm d={d} me={me} onChanged={onChanged} />}
      {canTriage && <hr className={styles.actionDivider} />}
      <AddNoteForm d={d} onChanged={onChanged} />
      {canClose && <hr className={styles.actionDivider} />}
      {canClose && <CloseForm d={d} me={me} onChanged={onChanged} />}
    </section>
  );
}

// ----- POST helper shared by the three forms -------------------------------
async function post(path: string, body: unknown): Promise<{ ok: true } | { ok: false; detail: string }> {
  try {
    const r = await fetch(apiUrl(path), {
      method: 'POST',
      headers: buildHeaders({ json: true }),
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const b = await r.json().catch(() => ({}));
      return { ok: false, detail: (b as { detail?: string }).detail ?? `Request failed (${r.status}).` };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, detail: String(e) };
  }
}

function TriageForm({ d, me, onChanged }: { d: DeviationDetailPayload; me: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const [classification, setClassification] = useState<DeviationClassification>(d.classification);
  const [investigator, setInvestigator] = useState(me);
  const [requiresCapa, setRequiresCapa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  async function submit() {
    if (!investigator.trim()) {
      setErr(t('deviation.actions.triage.needInvestigator', { defaultValue: 'An investigator is required.' }));
      return;
    }
    setBusy(true);
    setErr(undefined);
    const res = await post(`/api/v1/deviations/${encodeURIComponent(d.deviationId)}/triage`, {
      confirmedClassification: classification,
      investigatorUserId: investigator.trim(),
      requiresCapa,
    });
    setBusy(false);
    if (!res.ok) { setErr(res.detail); return; }
    onChanged();
  }

  return (
    <div className={styles.actionForm}>
      <h3 className={styles.panelTitle} style={{ fontSize: 'var(--font-size-sm)' }}>
        {t('deviation.actions.triage.title', { defaultValue: 'Triage' })}
      </h3>
      <div className={styles.actionField}>
        <label htmlFor="triage-class">{t('deviation.actions.triage.classification', { defaultValue: 'Confirmed classification' })}</label>
        <select id="triage-class" value={classification} onChange={(e) => setClassification(e.target.value as DeviationClassification)}>
          {CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>{t(`deviation.classification.${c}`, { defaultValue: c })}</option>
          ))}
        </select>
      </div>
      <div className={styles.actionField}>
        <label htmlFor="triage-investigator">{t('deviation.actions.triage.investigator', { defaultValue: 'Assign investigator (user id)' })}</label>
        <input id="triage-investigator" value={investigator} onChange={(e) => setInvestigator(e.target.value)} placeholder="user UUID" />
      </div>
      <div className={`${styles.actionField} ${styles.actionCheck}`}>
        <input id="triage-capa" type="checkbox" checked={requiresCapa} onChange={(e) => setRequiresCapa(e.target.checked)} />
        <label htmlFor="triage-capa" style={{ textTransform: 'none', letterSpacing: 0 }}>
          {t('deviation.actions.triage.requiresCapa', { defaultValue: 'Requires a CAPA' })}
        </label>
      </div>
      {err && <div className={styles.errorBanner} role="alert">{err}</div>}
      <div className={styles.actionRow}>
        <Button variant="primary" onClick={submit} disabled={busy}>
          {busy ? t('common.submitting', { defaultValue: 'Submitting…' }) : t('deviation.actions.triage.submit', { defaultValue: 'Triage deviation' })}
        </Button>
      </div>
    </div>
  );
}

function AddNoteForm({ d, onChanged }: { d: DeviationDetailPayload; onChanged: () => void }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<NoteKind>('OBSERVATION');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  async function submit() {
    if (!body.trim()) {
      setErr(t('deviation.actions.note.needBody', { defaultValue: 'Note text is required.' }));
      return;
    }
    setBusy(true);
    setErr(undefined);
    const res = await post(`/api/v1/deviations/${encodeURIComponent(d.deviationId)}/notes`, {
      noteKind: kind,
      body: body.trim(),
    });
    setBusy(false);
    if (!res.ok) { setErr(res.detail); return; }
    setBody('');
    onChanged();
  }

  return (
    <div className={styles.actionForm}>
      <h3 className={styles.panelTitle} style={{ fontSize: 'var(--font-size-sm)' }}>
        {t('deviation.actions.note.title', { defaultValue: 'Add investigation note' })}
      </h3>
      <div className={styles.actionField}>
        <label htmlFor="note-kind">{t('deviation.actions.note.kind', { defaultValue: 'Kind' })}</label>
        <select id="note-kind" value={kind} onChange={(e) => setKind(e.target.value as NoteKind)}>
          {NOTE_KINDS.map((k) => (
            <option key={k} value={k}>{t(`deviation.noteKind.${k}`, { defaultValue: k.replaceAll('_', ' ') })}</option>
          ))}
        </select>
      </div>
      <div className={styles.actionField}>
        <label htmlFor="note-body">{t('deviation.actions.note.body', { defaultValue: 'Note' })}</label>
        <textarea id="note-body" value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      {err && <div className={styles.errorBanner} role="alert">{err}</div>}
      <div className={styles.actionRow}>
        <Button variant="secondary" onClick={submit} disabled={busy}>
          {busy ? t('common.submitting', { defaultValue: 'Submitting…' }) : t('deviation.actions.note.submit', { defaultValue: 'Add note' })}
        </Button>
      </div>
    </div>
  );
}

function CloseForm({ d, me, onChanged }: { d: DeviationDetailPayload; me: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const isCritical = d.classification === 'CRITICAL';
  const isMinor = d.classification === 'MINOR';
  const [rca, setRca] = useState(d.rcaSummary ?? '');
  const [primary, setPrimary] = useState(me);
  const [secondary, setSecondary] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  async function submit() {
    if (!isMinor && rca.trim().length < 50) {
      setErr(t('deviation.actions.close.needRca', { defaultValue: 'A root-cause summary of at least 50 characters is required for Major/Critical closure (DEV-008).' }));
      return;
    }
    if (isCritical && (!primary.trim() || !secondary.trim() || primary.trim() === secondary.trim())) {
      setErr(t('deviation.actions.close.needDual', { defaultValue: 'Critical closure needs two distinct signatures (DEV-005).' }));
      return;
    }
    setBusy(true);
    setErr(undefined);
    const res = await post(`/api/v1/deviations/${encodeURIComponent(d.deviationId)}/close`, {
      classification: d.classification,
      rcaSummary: rca.trim() || null,
      closureSignaturePrimaryId: primary.trim() || null,
      closureSignatureSecondaryId: secondary.trim() || null,
    });
    setBusy(false);
    if (!res.ok) { setErr(res.detail); return; }
    onChanged();
  }

  return (
    <div className={styles.actionForm}>
      <h3 className={styles.panelTitle} style={{ fontSize: 'var(--font-size-sm)' }}>
        {t('deviation.actions.close.title', { defaultValue: 'Close deviation' })}
      </h3>
      <div className={styles.actionField}>
        <label htmlFor="close-rca">
          {t('deviation.actions.close.rca', { defaultValue: 'Root-cause summary' })}
          {!isMinor && ' '}{!isMinor && t('deviation.actions.close.rcaReq', { defaultValue: '(≥ 50 chars)' })}
        </label>
        <textarea id="close-rca" value={rca} onChange={(e) => setRca(e.target.value)} />
      </div>
      <div className={styles.actionField}>
        <label htmlFor="close-primary">{t('deviation.actions.close.primary', { defaultValue: 'Primary signer (user id)' })}</label>
        <input id="close-primary" value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="user UUID" />
      </div>
      {isCritical && (
        <div className={styles.actionField}>
          <label htmlFor="close-secondary">{t('deviation.actions.close.secondary', { defaultValue: 'Secondary signer (DEV-005, must differ)' })}</label>
          <input id="close-secondary" value={secondary} onChange={(e) => setSecondary(e.target.value)} placeholder="user UUID" />
        </div>
      )}
      {err && <div className={styles.errorBanner} role="alert">{err}</div>}
      <div className={styles.actionRow}>
        <Button variant="primary" onClick={submit} disabled={busy}>
          {busy ? t('common.submitting', { defaultValue: 'Submitting…' }) : t('deviation.actions.close.submit', { defaultValue: 'Close deviation' })}
        </Button>
      </div>
    </div>
  );
}
